import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, CreditCard, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const TYPE_LABELS = {
  en: { cash: "Cash", card: "Card", bank: "Bank", wallet: "Wallet", other: "Other" },
  ar: { cash: "نقدي", card: "بطاقة", bank: "بنك", wallet: "محفظة", other: "أخرى" },
};

const emptyRow = (methodId = "", accountId = "", amount = "") => ({
  method_id: methodId,
  account_id: accountId,
  amount,
});

function resolveAccountForMethod(methodId, paymentMethods, accounts) {
  const m = paymentMethods.find((x) => x.method_id === methodId);
  if (m?.target_account_id && accounts.some((a) => a.account_id === m.target_account_id)) {
    return m.target_account_id;
  }
  const flagged = accounts.find((a) => a.is_default);
  if (flagged) return flagged.account_id;
  return accounts[0]?.account_id || "";
}

export default function SplitPayModal({
  open,
  onClose,
  onConfirm,
  total,
  paymentMethods = [],
  accounts = [],
  defaultMethodId = "",
  defaultAccountId = "",
  lang = "ar",
  submitting = false,
}) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    const secondMethod =
      paymentMethods.find((m) => m.method_id !== defaultMethodId && m.type === "card")
      || paymentMethods.find((m) => m.method_id !== defaultMethodId)
      || paymentMethods[1]
      || paymentMethods[0];
    const half = total > 0 ? (total / 2).toFixed(2) : "";
    const remainder = total > 0 ? (total - Number(half)).toFixed(2) : "";
    setRows([
      emptyRow(defaultMethodId, defaultAccountId, half),
      emptyRow(
        secondMethod?.method_id || "",
        resolveAccountForMethod(secondMethod?.method_id, paymentMethods, accounts),
        remainder,
      ),
    ]);
  }, [open, total, defaultMethodId, defaultAccountId, paymentMethods, accounts]);

  const allocated = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [rows],
  );
  const remaining = +(total - allocated).toFixed(2);
  const canSubmit = rows.length >= 2
    && rows.every((r) => r.method_id && r.account_id && parseFloat(r.amount) > 0)
    && Math.abs(remaining) < 0.02;

  const typeLabels = TYPE_LABELS[lang === "ar" ? "ar" : "en"];

  const onMethodChange = (idx, methodId) => {
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      return {
        ...r,
        method_id: methodId,
        account_id: resolveAccountForMethod(methodId, paymentMethods, accounts),
      };
    }));
  };

  const fillRemaining = (idx) => {
    const otherSum = rows.reduce((s, r, i) => (i === idx ? s : s + (parseFloat(r.amount) || 0)), 0);
    const fill = Math.max(0, +(total - otherSum).toFixed(2));
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, amount: fill ? fill.toFixed(2) : "" } : r)));
  };

  const addRow = () => {
    const fallback = paymentMethods[0];
    setRows((prev) => [
      ...prev,
      emptyRow(
        fallback?.method_id || "",
        resolveAccountForMethod(fallback?.method_id, paymentMethods, accounts),
        remaining > 0 ? remaining.toFixed(2) : "",
      ),
    ]);
  };

  const removeRow = (idx) => {
    if (rows.length <= 2) return;
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleConfirm = () => {
    if (!canSubmit) return;
    const splits = rows.map((r) => {
      const m = paymentMethods.find((x) => x.method_id === r.method_id);
      return {
        method_id: r.method_id,
        account_id: r.account_id,
        payment_method: m?.type || "cash",
        amount: parseFloat(r.amount),
      };
    });
    onConfirm(splits);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="split-pay-modal">
        <DialogHeader>
          <DialogTitle className="font-heading gold-text flex items-center gap-2">
            <CreditCard size={18}/>
            {lang === "ar" ? "دفع مقسّم" : "Split Payment"}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            {lang === "ar"
              ? "قسّم المبلغ على أكثر من طريقة دفع وصندوق."
              : "Divide the total across multiple payment methods and cashboxes."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg bg-[var(--bg-alt)] border border-[var(--border-subtle)] px-3 py-2 text-sm">
            <span className="text-[var(--text-muted)]">{lang === "ar" ? "إجمالي الفاتورة" : "Invoice total"}</span>
            <span className="font-heading gold-text num-display">{total.toFixed(2)} {lang === "ar" ? "ر.ق" : "QAR"}</span>
          </div>

          {rows.map((row, idx) => (
            <div key={idx} className="rounded-xl border border-[var(--border)] p-3 space-y-2 bg-[var(--surface-2)]" data-testid={`split-pay-row-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                  {lang === "ar" ? `دفعة ${idx + 1}` : `Part ${idx + 1}`}
                </span>
                {rows.length > 2 && (
                  <button type="button" onClick={() => removeRow(idx)} className="text-[var(--text-muted)] hover:text-rose-600 p-1">
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[var(--text-muted)] uppercase block mb-1">
                    {lang === "ar" ? "طريقة الدفع" : "Payment method"}
                  </label>
                  <select
                    className="input-dark text-sm py-1.5 w-full"
                    value={row.method_id}
                    onChange={(e) => onMethodChange(idx, e.target.value)}
                    data-testid={`split-pay-method-${idx}`}
                  >
                    <option value="">{lang === "ar" ? "— اختر —" : "— Select —"}</option>
                    {paymentMethods.map((m) => {
                      const typeLabel = typeLabels[m.type] || m.type;
                      return (
                        <option key={m.method_id} value={m.method_id}>
                          {m.name} {m.name.toLowerCase() !== typeLabel.toLowerCase() ? `(${typeLabel})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[var(--text-muted)] uppercase block mb-1">
                    {lang === "ar" ? "الصندوق / البنك" : "Cashbox / Bank"}
                  </label>
                  <select
                    className="input-dark text-sm py-1.5 w-full"
                    value={row.account_id}
                    onChange={(e) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, account_id: e.target.value } : r)))}
                    data-testid={`split-pay-account-${idx}`}
                  >
                    <option value="">{lang === "ar" ? "— اختر —" : "— Select —"}</option>
                    {accounts.map((a) => (
                      <option key={a.account_id} value={a.account_id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-[10px] text-[var(--text-muted)] uppercase block mb-1">
                    {lang === "ar" ? "المبلغ" : "Amount"}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-dark text-sm py-1.5 w-full num-display"
                    value={row.amount}
                    onChange={(e) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, amount: e.target.value } : r)))}
                    data-testid={`split-pay-amount-${idx}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => fillRemaining(idx)}
                  className="btn-secondary px-3 py-2 rounded-md text-[10px] whitespace-nowrap"
                  data-testid={`split-pay-fill-${idx}`}
                >
                  {lang === "ar" ? "الباقي" : "Fill rest"}
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addRow}
            className="w-full py-2 rounded-lg border border-dashed border-[var(--border)] text-xs font-medium text-[var(--text-muted)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition flex items-center justify-center gap-1.5"
            data-testid="split-pay-add-row"
          >
            <Plus size={14}/>
            {lang === "ar" ? "إضافة دفعة" : "Add payment part"}
          </button>

          <div className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 border ${
            Math.abs(remaining) < 0.02
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}>
            <span>{lang === "ar" ? "المتبقي" : "Remaining"}</span>
            <span className="num-display font-semibold">{remaining.toFixed(2)}</span>
          </div>

          {!canSubmit && rows.some((r) => r.amount) && Math.abs(remaining) >= 0.02 && (
            <div className="text-[11px] text-amber-700 flex items-center gap-1">
              <AlertCircle size={12}/>
              {lang === "ar" ? "يجب أن يساوي مجموع الدفعات إجمالي الفاتورة" : "Split amounts must equal the invoice total"}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-md text-sm" disabled={submitting}>
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit || submitting}
            className="btn-gold px-4 py-2 rounded-md text-sm disabled:opacity-50"
            data-testid="split-pay-confirm"
          >
            {submitting
              ? (lang === "ar" ? "جاري الدفع..." : "Processing...")
              : (lang === "ar" ? "تأكيد الدفع المقسّم" : "Confirm split pay")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
